import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";

import { useState } from "react";
import { z } from "zod";
import { submitLead } from "@/lib/submissions";
import { trackEvent } from "@/lib/analytics";
import { InternalLinksHub } from "@/components/internal-links-hub";
import { ConsentCheckbox, DocReviewCheckbox } from "@/components/consent-checkbox";

export const Route = createFileRoute("/contact-hr-experts")({
  head: () => ({
    meta: [
      { title: "Contact Our HR Experts, Free Consultation for UK SMEs" },
      {
        name: "description",
        content:
          "Book a free 30-minute consultation with a senior HR advisor or employment solicitor. Response within 2 business hours.",
      },
      { property: "og:title", content: "Contact Our HR Experts, Free Consultation for UK SMEs" },
      { property: "og:description", content: "Free HR & employment law consultation for UK SMEs." },
      { property: "og:url", content: "https://www.hrservicecentre.co.uk/contact-hr-experts" },
    ],
    links: [{ rel: "canonical", href: "https://www.hrservicecentre.co.uk/contact-hr-experts" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact EmployerHQ",
          url: "https://www.hrservicecentre.co.uk/contact-hr-experts",
          description:
            "Book a free 30-minute consultation with a senior HR advisor or employment solicitor. Response within 2 business hours.",
          isPartOf: { "@id": "https://www.hrservicecentre.co.uk/#website" },
          mainEntity: {
            "@id": "https://www.hrservicecentre.co.uk/#organization",
            "@type": "Organization",
            contactPoint: [
              {
                "@type": "ContactPoint",
                contactType: "customer service",
                url: "https://www.hrservicecentre.co.uk/contact-hr-experts",
                areaServed: "GB",
                availableLanguage: ["English"],
              },
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.hrservicecentre.co.uk/" },
            { "@type": "ListItem", position: 2, name: "Contact", item: "https://www.hrservicecentre.co.uk/contact-hr-experts" },
          ],
        }),
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [docReview, setDocReview] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    headcount: "1–10 employees",
    topic: "HR Outsourcing",
    message: "",
  });
  const update =
    (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const schema = z.object({
      name: z.string().trim().min(1).max(100),
      company: z.string().trim().min(1).max(120),
      email: z.string().trim().email().max(255),
      phone: z.string().trim().max(40).optional(),
      headcount: z.string().max(40),
      topic: z.string().max(80),
      message: z.string().trim().min(1).max(2000),
    });
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      setServerError(parsed.error.issues[0]?.message ?? "Please check the form.");
      return;
    }
    setSubmitting(true);
    setServerError(null);
    try {
      await submitLead({
        docReview,
        name: parsed.data.name,
        email: parsed.data.email,
        company: parsed.data.company,
        headcount: parsed.data.headcount,
        topic: parsed.data.topic,
        message: parsed.data.message + (parsed.data.phone ? `\n\nPhone: ${parsed.data.phone}` : ""),
        consent,
        source: "contact",
        page_path: "/contact-hr-experts",
      });
      trackEvent("form_submit", {
        category: "lead",
        action: "contact_enquiry",
        label: parsed.data.topic,
        properties: {
          form_type: "contact",
          topic: parsed.data.topic,
          headcount: parsed.data.headcount,
        },
      });
      setSubmitted(true);
    } catch {
      setServerError("We couldn't send your message. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <PageHero
      breadcrumbSchema={false}
      breadcrumbs={[
        { label: "Home", path: "/" },
        { label: "Contact", path: "/contact-hr-experts" },
      ]}
      eyebrow="Contact"
      title="Contact EmployerHQ, UK HR & Employment Law Specialists"
      subtitle="Speak to a UK HR and employment law specialist today."
      intro="Free 30-minute consultation. No hard sell, no obligation, no jargon. We'll come back to you within 2 business hours."
    />
    <section className="px-6 py-12 lg:px-8 lg:py-16">
      <div className="mx-auto max-w-3xl">
        <div className="rounded-3xl border border-border bg-card p-8 shadow-sm sm:p-10">
            {submitted ? (
              <div className="py-10 text-center">
                <p className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-accent/10 text-2xl text-accent">
                  ✓
                </p>
                <h2 className="font-serif text-2xl text-brand">Message received</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  A senior advisor will be in touch within 2 business hours.
                </p>
              </div>
            ) : (
              <form
                className="grid grid-cols-1 gap-5 sm:grid-cols-2"
                onSubmit={onSubmit}
                noValidate
              >
                <Field label="Full name">
                  <input required type="text" value={form.name} onChange={update("name")} maxLength={100} className={inputCls} />
                </Field>
                <Field label="Company name">
                  <input required type="text" value={form.company} onChange={update("company")} maxLength={120} className={inputCls} />
                </Field>
                <Field label="Work email">
                  <input required type="email" value={form.email} onChange={update("email")} maxLength={255} className={inputCls} />
                </Field>
                <Field label="Phone">
                  <input type="tel" value={form.phone} onChange={update("phone")} maxLength={40} className={inputCls} />
                </Field>
                <Field label="Company size">
                  <select value={form.headcount} onChange={update("headcount")} className={inputCls}>
                    <option>1–10 employees</option>
                    <option>11–50 employees</option>
                    <option>51–250 employees</option>
                    <option>250+ employees</option>
                  </select>
                </Field>
                <Field label="Topic">
                  <select value={form.topic} onChange={update("topic")} className={inputCls}>
                    <option>HR Outsourcing</option>
                    <option>Employment Law Advice</option>
                    <option>Tribunal Defence</option>
                    <option>Redundancy / TUPE</option>
                    <option>HR Health Check</option>
                    <option>Other</option>
                  </select>
                </Field>
                <Field label="How can we help?" full>
                  <textarea required rows={4} value={form.message} onChange={update("message")} maxLength={2000} className={inputCls} />
                </Field>
                <div className="sm:col-span-2">
                  <ConsentCheckbox id="consent-contact" checked={consent} onChange={setConsent} className="mb-4" />
                  <DocReviewCheckbox id="doc-review-contact" checked={docReview} onChange={setDocReview} />
                  {serverError && <p className="mb-3 text-sm text-destructive">{serverError}</p>}
                  <button
                    type="submit"
                    disabled={submitting || !consent}
                    className="inline-flex w-full items-center justify-center rounded-md bg-brand py-3 text-sm font-medium text-brand-foreground transition hover:brightness-110 disabled:opacity-60 sm:w-auto sm:px-8"
                  >
                    {submitting ? "Sending…" : "Send message"}
                  </button>
                  <p className="mt-3 text-xs text-muted-foreground">
                    By submitting this form you agree to our privacy policy. We only use your details to respond to your
                    enquiry.
                  </p>
                </div>
              </form>
            )}
        </div>
      </div>
    </section>
    <InternalLinksHub heading="While you're here, explore HR support for UK employers" />
    </>
  );
}

const inputCls =
  "mt-1 w-full rounded-md border border-border bg-background px-3 py-2.5 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent";

function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-xs font-medium text-foreground">{label}</span>
      {children}
    </label>
  );
}
